import Container from "~/components/atoms/Container";
import { Suspense, useCallback, useRef, useState } from "react";
import { Search } from "lucide-react";
import CustomPagination from "~/components/blocks/Pagination/Pagination";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "~/components/ui/custom-radix-components/pagination";

import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { LazyLoader } from "~/lib/utils/LazyLoader";
import BlogHeroBanner from "../HeroBanners/BlogHeroBanner";
import TenantHelpGuide from "../Sections/TenantHelpGuide";
import TrainingVideosScection from "../Sections/TrainingVideosScection";
import PodcastSection from "../Sections/PodcastSection";
// import AreaGuidesSection from "../Sections/AreaGuidesSection";
import GuideCard from "../Card/GuideCard";
import { Await } from "@remix-run/react";
import { Skeleton } from "~/components/ui/skeleton";
import BlogCard from "../Card/BlogCard";
import { OhButton } from "~/components/ui/custom-radix-components/button";
import { Link } from "@remix-run/react";
import { getRegionUrlPrefix } from "~/lib/helpers/slugHelper";
import BlogSkeletonCard from "../Card/BlogSkeletonCard";
import NoResults from "../Card/NoResultCard";

interface MobileLayoutProps {
  getBlogs: (categories: string[], searchText: string, page?: number) => void;
  renderBlogs: () => JSX.Element | JSX.Element[];
  searchText: string;
  content: any;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  media: any;
  blogsData: any;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  params: any;
  country: string;
  loading: boolean;
  currentMedia?: any;
  setCurrentMedia?: (media: any) => void;
}

export default function MobileLayout({
  getBlogs,
  searchText,
  content,
  onSearch,
  media,
  blogsData,
  currentPage,
  setCurrentPage,
  params,
  country,
  loading,
}: MobileLayoutProps) {
  const ITEMS_PER_PAGE = 5;
  const totalPages = Math.ceil(blogsData?.totalBlogs / ITEMS_PER_PAGE);
  const [selected, setSelected] = useState<string[]>(['All']);
  const ref = useRef(null);


  const clearFilters = useCallback(() => {
    setSelected(['All']);
    setCurrentPage(1);

    if (onSearch) {
      onSearch({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
    }

    getBlogs([], "", 1);

  }, [getBlogs, onSearch]);
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    getBlogs([], searchText, newPage);
  }, [totalPages, searchText, getBlogs]);

  return (
    <>
      <Container htmlFor="ultra-wide">

        <h1 className="text-xl sm:text-2xl text-oh-black font-semibold mt-4 line-clamp-2 px-4 md:px-0">
          {`News & Insights on Flexible Office Spaces in ${country === "United States" || country === "United Kingdom" ? `the ${country}` : country
            }`}
        </h1>
        {/* Search Section */}
        <div className="w-full px-4 py-6 ">
          <div className="flex justify-between mb-6">
            <div
              ref={ref}
              id="search-container"
              className="flex items-center w-full max-w-lg h-[39px] rounded-md border border-gray-300 overflow-hidden">
              <Search className="ml-3 h-4 w-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search by title name or context"
                className="w-full px-3 py-2 outline-none bg-white text-sm"
                onChange={onSearch}
                value={searchText}
              />
              <button className="bg-black text-white px-4 py-2 text-sm">
                SEARCH
              </button>
            </div>
          </div>

          {/* Blogs Section with Await + Suspense */}
          {blogsData === null || loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 my-4">
              {Array.from({ length: ITEMS_PER_PAGE }, (_, idx) => (
                <BlogSkeletonCard
                  displayMode="grid"
                />
              ))}
            </div>
          ) : blogsData.blogs.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 my-4">
                {blogsData.blogs.map((article: any, idx: number) => (
                  <BlogCard country={country} key={article.id || idx} article={article} />
                ))}
              </div>

              {/* Pagination */}
              {Math.ceil(blogsData.pageInfo.totalCount / ITEMS_PER_PAGE) > 1 && (
                <div className="flex justify-center my-4">
                  <Pagination className="max-w-[550px] w-full justify-center">
                    <PaginationContent className="flex gap-0 items-center">
                      {currentPage > 1 ? (
                        <PaginationItem className="cursor-pointer">
                          <PaginationPrevious
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();

                              handlePageChange(currentPage - 1);

                              setTimeout(() => {
                                document
                                  .getElementById("search-container")
                                  ?.scrollIntoView({ behavior: "smooth" });
                              }, 0);
                            }}
                          />
                        </PaginationItem>
                      ) : (
                        <div className="w-[60px]" />
                      )}

                      <CustomPagination
                        currentPage={currentPage}
                        handlePageChange={handlePageChange}
                        totalPages={Math.ceil(
                          blogsData.pageInfo.totalCount / ITEMS_PER_PAGE
                        )}
                        scrollRef={ref}
                      />

                      {currentPage <
                        Math.ceil(blogsData.pageInfo.totalCount / ITEMS_PER_PAGE) ? (
                        <PaginationItem className="cursor-pointer">
                          {/* <PaginationNext
                            to="#"
                            onClick={() => {
                              handlePageChange(currentPage + 1);
                              document
                                .getElementById('search-container')
                                ?.scrollIntoView({ behavior: 'smooth' });
                            }}
                          /> */}

                          <PaginationNext
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();

                              handlePageChange(currentPage + 1);

                              setTimeout(() => {
                                document
                                  .getElementById("search-container")
                                  ?.scrollIntoView({ behavior: "smooth" });
                              }, 0);
                            }}
                          />
                        </PaginationItem>
                      ) : (
                        <div className="w-[40px]" />
                      )}
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="py-5">
              <NoResults onClear={clearFilters} />
            </div>
          )}

          {/* Tenant Help Guide */}
          <LazyLoader>
            {content["tenant-guide"]?.length ? (
              <TenantHelpGuide guides={content["tenant-guide"]} country={country} />
            ) : null}
          </LazyLoader>

          {/* Host Hub Section */}
          {content["host-guide"]?.length ? (
            <LazyLoader>
              <section>
                <div className="flex justify-between items-end my-6">
                  <h3 className="text-lg font-semibold leading-none">
                    Host Hub
                  </h3>
                </div>
                <div>
                  {content["host-guide"].map((article: any, idx: number) => (
                    <Link to={`${getRegionUrlPrefix(article?.country !== null ? article?.country : country)}/blog/${article.slug}`} key={idx} target="_blank">
                      <GuideCard country={country} article={article} />
                    </Link>
                  ))}
                </div>
              </section>
            </LazyLoader>
          ) : null}

          {/* Media Sections (deferred) */}
          <Suspense fallback={<Skeleton className="min-h-80" />}>
            <Await resolve={media}>
              {(resolvedMedia) => (
                <LazyLoader>
                  {resolvedMedia?.video?.length ? (
                    <TrainingVideosScection media={resolvedMedia.video} country={country} />
                  ) : null}

                  {/* {content["area-guide"]?.length ? (
                    <AreaGuidesSection
                      guides={content["area-guide"]}
                      params={params}
                      country={country}
                    />
                  ) : null} */}

                  {resolvedMedia?.audio?.length ? (
                    <PodcastSection media={resolvedMedia.audio || []} country={country} />
                  ) : null}
                </LazyLoader>
              )}
            </Await>
          </Suspense>
        </div>
      </Container>

      {/* Newsletter Subscription Banner */}
      <LazyLoader>
        <div
          className="bg-cover bg-center py-16 px-4 text-white text-center"
          style={{
            backgroundImage: `url('${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/blogs/bg-banner.avif')`,
          }}
        >
          <h2 className="text-2xl font-bold mb-2">
            Subscribe to Office Hub’s Newsletter
          </h2>
          <p className="mb-6">
            Get the best in industry news, delivered to your inbox.
          </p>
          <div className="max-w-md mx-auto flex">
            <Input
              type="email"
              placeholder="Enter your email here"
              className="flex-1 rounded-tr-none rounded-br-none"
            />
            <OhButton className="rounded-tl-none rounded-bl-none">
              Subscribe
            </OhButton>
          </div>
        </div>
      </LazyLoader>
    </>
  );
}
